import { agreementGrantsRelation, type AgreementPermission } from './agreementPermissions'
import { CONFIG_AREAS, type ConfigArea } from './configAreas'
import { CORE_DATA_DIMENSIONS, type CoreDataDimension } from './coreDataDimensions'
import { mapUserToOrganisationRole, type UserRoleTierConfig } from './organisationRoles'
import { requestGrantsRelation, type RequestPermission } from './requestPermissions'
import { DEFAULT_SUPPLIER_PERMISSIONS, supplierGrantsRelation, type SupplierPermission } from './supplierPermissions'
import type { FgaAssignableRelation, FgaObjectRef, FgaObjectType, FgaRelation, FgaTuple } from './generated/model'
import type { GrantFixedLevel } from './grantFixedSets'
import type { PermissionSetEntity } from './permissionSetEntities'

// ---------------------------------------------------------------------------
// Object references
// ---------------------------------------------------------------------------

/**
 * Builds an object-reference string (`type:id`) for any model object type.
 *
 * The id is lowercased: OpenFGA matches object refs as exact strings while Postgres compares uuids
 * case-insensitively, so a caller-supplied uppercase uuid would otherwise address a different
 * object than the synced tuples. Every id in the model is a uuid or a lowercase composite, and
 * every write goes through these builders too, so normalizing here keeps checks and tuples on one
 * canonical form.
 */
export const objectRef = <T extends FgaObjectType>(type: T, id: string): FgaObjectRef<T> => `${type}:${id.toLowerCase()}`

/** The id half of an object reference. A ref carrying no type prefix is already an id. */
export const objectIdOf = (ref: string): string => ref.slice(ref.indexOf(':') + 1)

/** The type half of an object reference, or '' for a ref carrying no type prefix. */
export const objectTypeOf = (ref: string): string => (ref.includes(':') ? ref.slice(0, ref.indexOf(':')) : '')

export const organisationObject = (orgId: string): FgaObjectRef<'organisation'> => objectRef('organisation', orgId)

export const configAreaObject = (orgId: string, area: ConfigArea): FgaObjectRef<'config_area'> =>
  objectRef('config_area', `${orgId}/${area}`)

export const groupObject = (groupId: string): FgaObjectRef<'group'> => objectRef('group', groupId)

export const requestObject = (requestId: string): FgaObjectRef<'request'> => objectRef('request', requestId)

export const supplierObject = (supplierId: string): FgaObjectRef<'supplier'> => objectRef('supplier', supplierId)

export const agreementObject = (agreementId: string): FgaObjectRef<'agreement'> => objectRef('agreement', agreementId)

export const roleObject = (roleId: string): FgaObjectRef<'role'> => objectRef('role', roleId)

/** A scoping dimension instance — one per (organisation, dimension). */
export const scopeDimensionObject = (orgId: string, dimension: CoreDataDimension): FgaObjectRef<'scope_dimension'> =>
  objectRef('scope_dimension', `${orgId}/${dimension}`)

/** A scope value — the id is the departments.id / subsidiaries.id row uuid. */
export const scopeValueObject = (valueId: string): FgaObjectRef<'scope_value'> => objectRef('scope_value', valueId)

/**
 * Everything that differs between the entities, in one place. Every per-entity lookup reads
 * from here, so adding an entity is one entry and a missing one is a compile error — the alternative
 * is a scattered literal, and a read against the wrong object type comes back EMPTY rather than
 * throwing, so the set would silently report no grants.
 */
export const GRANT_MODEL_BY_ENTITY = {
  request: {
    grantType: 'request_grant',
    permissionSetType: 'request_permission_set',
    /** Where the entity's grants hang off a shared anchor, and off the record itself. */
    anchoredRelation: 'request_grant',
    dynamicRelation: 'matching_request_grant',
    orgWideViewRelation: 'can_view_all_requests',
    clearanceRelation: 'confidential_request_access',
    recordRelation: 'grant',
    recordType: 'request',
  },
  supplier: {
    grantType: 'supplier_grant',
    permissionSetType: 'supplier_permission_set',
    anchoredRelation: 'supplier_grant',
    dynamicRelation: 'matching_supplier_grant',
    orgWideViewRelation: 'can_view_all_suppliers',
    clearanceRelation: 'confidential_supplier_access',
    recordRelation: 'grant',
    recordType: 'supplier',
  },
  agreement: {
    grantType: 'agreement_grant',
    permissionSetType: 'agreement_permission_set',
    anchoredRelation: 'agreement_grant',
    dynamicRelation: 'matching_agreement_grant',
    orgWideViewRelation: 'can_view_all_agreements',
    clearanceRelation: 'confidential_agreement_access',
    recordRelation: 'grant',
    recordType: 'agreement',
  },
  // The mapped constraint correlates each entry with its OWN record type (the entity name is the
  // record type), so recordRelation is checked against the right relation list per entity — a
  // hand-widened intersection would silently stop checking an entity someone forgot to add to it.
} as const satisfies {
  [Entity in PermissionSetEntity]: {
    grantType: FgaObjectType
    permissionSetType: FgaObjectType
    anchoredRelation: FgaRelation<'organisation'> & FgaRelation<'scope_value'>
    dynamicRelation: FgaRelation<'scope_dimension'>
    orgWideViewRelation: FgaRelation<'organisation'>
    clearanceRelation: FgaAssignableRelation<'organisation'>
    recordRelation: FgaRelation<Entity>
    recordType: Entity
  }
}

export const grantObject = <Entity extends PermissionSetEntity>(
  entity: Entity,
  grantId: string
): FgaObjectRef<(typeof GRANT_MODEL_BY_ENTITY)[Entity]['grantType']> =>
  objectRef(GRANT_MODEL_BY_ENTITY[entity].grantType, grantId)

/**
 * The id carries no organisation, so callers must resolve it through an org-scoped metadata read
 * before naming it in a tuple — a ref alone cannot be confined to one tenant.
 */
export const permissionSetObject = <Entity extends PermissionSetEntity>(
  entity: Entity,
  permissionSetId: string
): FgaObjectRef<(typeof GRANT_MODEL_BY_ENTITY)[Entity]['permissionSetType']> =>
  objectRef(GRANT_MODEL_BY_ENTITY[entity].permissionSetType, permissionSetId)

// ---------------------------------------------------------------------------
// Permission sets — what a grant may confer
// ---------------------------------------------------------------------------

export const buildPermissionSetAnchorTuple = (entity: PermissionSetEntity, orgId: string, permissionSetId: string): FgaTuple => ({
  user: organisationObject(orgId),
  relation: 'structural_organisation',
  object: permissionSetObject(entity, permissionSetId),
})

// These stay per-entity: FgaTuple correlates relation with object type, and a generic version cannot
// prove a request permission lands on a request set without a cast.

/** The tuple enabling one permission on a request set. Its absence is what "not granted" means. */
export const requestPermissionTuple = (permissionSetId: string, permission: RequestPermission): FgaTuple => ({
  user: 'user:*',
  relation: requestGrantsRelation(permission),
  object: permissionSetObject('request', permissionSetId),
})

/** The tuple enabling one permission on a supplier set. */
export const supplierPermissionTuple = (permissionSetId: string, permission: SupplierPermission): FgaTuple => ({
  user: 'user:*',
  relation: supplierGrantsRelation(permission),
  object: permissionSetObject('supplier', permissionSetId),
})

export const agreementPermissionTuple = (permissionSetId: string, permission: AgreementPermission): FgaTuple => ({
  user: 'user:*',
  relation: agreementGrantsRelation(permission),
  object: permissionSetObject('agreement', permissionSetId),
})

/**
 * A set carrying {@link DEFAULT_SUPPLIER_PERMISSIONS} — see there for why this is not what the API
 * provisions.
 */
export const buildDefaultSupplierPermissionSetTuples = (orgId: string, permissionSetId: string): FgaTuple[] => [
  buildPermissionSetAnchorTuple('supplier', orgId, permissionSetId),
  ...DEFAULT_SUPPLIER_PERMISSIONS.map((permission) => supplierPermissionTuple(permissionSetId, permission)),
]

// ---------------------------------------------------------------------------
// Grants — a subject, what it confers, and where it applies
// ---------------------------------------------------------------------------

/**
 * Exactly one of a fixed set or an organisation-authored one, never both: a custom set may confer LESS
 * than Viewer, so unioning them would make the reduced set unreachable. The model cannot enforce that
 * exclusivity — this union is what makes a combination unrepresentable.
 */
export type GrantConfiguration = { kind: 'fixed'; level: GrantFixedLevel } | { kind: 'custom'; permissionSetId: string }

/** The subject and permission source of one grant. A custom set id must be org-resolved first. */
export const buildGrantTuples = <Entity extends PermissionSetEntity>(
  entity: Entity,
  grant: { grantId: string; subject: string; configuration: GrantConfiguration }
): FgaTuple[] => {
  const object = grantObject(entity, grant.grantId)
  return [
    { user: grant.subject, relation: 'subject', object },
    grant.configuration.kind === 'fixed'
      ? { user: 'user:*', relation: `${grant.configuration.level}_set`, object }
      : { user: permissionSetObject(entity, grant.configuration.permissionSetId), relation: 'custom_set', object },
  ]
}

/** Where a grant applies. `record` is the "Involved Only" scope on suppliers, direct access on requests. */
export type GrantScope =
  | { kind: 'organisation'; orgId: string }
  | { kind: 'value'; valueId: string }
  | { kind: 'dynamic'; orgId: string; dimension: CoreDataDimension }
  | { kind: 'record'; entityId: string }

/**
 * The scope edge attaching a grant where it applies. Relations come from {@link GRANT_MODEL_BY_ENTITY} rather
 * than a second copy: a divergence would not throw — an unmatched relation reads as "no scope", so the
 * grant silently vanishes from every read instead of failing.
 */
export const buildGrantScopeTuple = <Entity extends PermissionSetEntity>(
  entity: Entity,
  grantId: string,
  scope: GrantScope
): FgaTuple => {
  const user = grantObject(entity, grantId)
  const entityConfig = GRANT_MODEL_BY_ENTITY[entity]
  switch (scope.kind) {
    case 'organisation':
      return { user, relation: entityConfig.anchoredRelation, object: organisationObject(scope.orgId) }
    case 'value':
      return { user, relation: entityConfig.anchoredRelation, object: scopeValueObject(scope.valueId) }
    case 'dynamic':
      return { user, relation: entityConfig.dynamicRelation, object: scopeDimensionObject(scope.orgId, scope.dimension) }
    case 'record':
      return {
        user,
        relation: entityConfig.recordRelation,
        object: objectRef(entityConfig.recordType, scope.entityId),
      }
  }
}

/**
 * The floor and ceiling an admin can put a user on for one supplier, consulting no permission set;
 * anything between them needs a grant. Not `derived_` prefixed on purpose — convergence deletes
 * unexpected `derived_` tuples, and an admin's grant is never in the expected set.
 */
export const SUPPLIER_DIRECT_LEVELS = ['viewer', 'owner'] as const

export type SupplierDirectLevel = (typeof SUPPLIER_DIRECT_LEVELS)[number]

export const buildSupplierDirectGrantTuple = (grant: {
  supplierId: string
  subject: string
  level: SupplierDirectLevel
}): FgaTuple => ({ user: grant.subject, relation: grant.level, object: supplierObject(grant.supplierId) })

// ---------------------------------------------------------------------------
// Derived entity tuples — what the sync pipeline converges
// ---------------------------------------------------------------------------

/**
 * The derived tuples a live request holds. Shared by the scanner and sweeper so the
 * derivation lives in one place.
 *
 * A confidential record carries the `_confidential` twins of its organisation and scope edges
 * INSTEAD of the normal ones. Every broad access arm hangs off the normal edges, so the swap is
 * what cuts broad access; the twins are read only by the `conf_` relations (permission AND
 * confidential_*_access), which is what lets clearance holders back in at their existing level.
 */
/** Everything a request must supply to derive its tuples. Its authz-state reader derives from this. */
export type RequestTupleInput = {
  id: string
  isConfidential: boolean
  requestorUserId: string
  collaboratorUserIds: readonly string[]
  collaboratorGroupIds: readonly string[]
  /** Core-data value ids the request is scoped to (department incl. renewal fallback, buyer legal entity). */
  scopeValueIds?: readonly string[]
  /** requests.request_supplier, when set — routes the supplier's view_related_requests to the request. */
  supplierId?: string | null
  agreementIds: readonly string[]
}

export const buildRequestTuples = (orgId: string, request: RequestTupleInput): FgaTuple[] => {
  const object = requestObject(request.id)
  const organisationRelation = request.isConfidential ? 'derived_organisation_confidential' : 'derived_organisation'
  const scopeRelation = request.isConfidential ? 'derived_scope_confidential' : 'derived_scope'
  // The supplier hop carries the supplier's broad arms, so it swaps like the other two.
  const supplierRelation = request.isConfidential ? 'derived_supplier_confidential' : 'derived_supplier'
  const agreementRelation = request.isConfidential ? 'derived_agreement_confidential' : 'derived_agreement'
  return [
    { user: organisationObject(orgId), relation: organisationRelation, object },
    { user: `user:${request.requestorUserId}`, relation: 'derived_requester', object },
    ...request.collaboratorUserIds.map(
      (userId): FgaTuple => ({ user: `user:${userId}`, relation: 'derived_collaborator', object })
    ),
    ...request.collaboratorGroupIds.map(
      (groupId): FgaTuple => ({ user: `${groupObject(groupId)}#derived_member`, relation: 'derived_collaborator', object })
    ),
    ...(request.scopeValueIds ?? []).map(
      (valueId): FgaTuple => ({ user: scopeValueObject(valueId), relation: scopeRelation, object })
    ),
    ...(request.supplierId != null
      ? [{ user: supplierObject(request.supplierId), relation: supplierRelation, object } satisfies FgaTuple]
      : []),
    ...request.agreementIds.map(
      (agreementId): FgaTuple => ({ user: agreementObject(agreementId), relation: agreementRelation, object })
    ),
  ]
}

/**
 * The derived tuples a live supplier holds. Shared by the scanner, sweeper and seeder so
 * none can drift from the others.
 *
 * Admin-granted access is deliberately absent — re-deriving `viewer`/`owner` or grants here would
 * have convergence treat them as drift.
 */
/** Everything a supplier must supply to derive its tuples. Its authz-state reader derives from this. */
export type SupplierTupleInput = {
  id: string
  isConfidential: boolean
  assignments: readonly { userId: string; kind: string }[]
  /** Subsidiary ids from the supplier's live organisation_supplier_profiles rows. */
  subsidiaryScopeIds?: readonly string[]
  agreementIds: readonly string[]
}

export const buildSupplierTuples = (orgId: string, supplier: SupplierTupleInput): FgaTuple[] => {
  const object = supplierObject(supplier.id)
  // A confidential record swaps its organisation and scope edges for the twins — see buildRequestTuples.
  const organisationRelation = supplier.isConfidential ? 'derived_organisation_confidential' : 'derived_organisation'
  const scopeRelation = supplier.isConfidential ? 'derived_scope_confidential' : 'derived_scope'
  const agreementRelation = supplier.isConfidential ? 'derived_agreement_confidential' : 'derived_agreement'
  // The kind no longer decides what an assignee may do, so two rows for one user collapse to one tuple.
  const assigneeUserIds = [...new Set(supplier.assignments.map((assignment) => assignment.userId))]
  return [
    { user: organisationObject(orgId), relation: organisationRelation, object },
    ...assigneeUserIds.map((userId): FgaTuple => ({ user: `user:${userId}`, relation: 'derived_contact', object })),
    ...(supplier.subsidiaryScopeIds ?? []).map(
      (valueId): FgaTuple => ({ user: scopeValueObject(valueId), relation: scopeRelation, object })
    ),
    ...supplier.agreementIds.map(
      (agreementId): FgaTuple => ({ user: agreementObject(agreementId), relation: agreementRelation, object })
    ),
  ]
}

/**
 * The derived tuples a live agreement holds. Shared by the scanner, sweeper and seeder so
 * none can drift from the others.
 */
/** Everything an agreement must supply to derive its tuples. Its authz-state reader derives from this. */
export type AgreementTupleInput = {
  id: string
  isConfidential: boolean
  /** agreements.owner_id ∪ agreement_additional_owners, deduped here so callers need not. */
  ownerUserIds: readonly string[]
  /** agreements.supplier_id — NOT NULL, so exactly one edge; routes the supplier's view_related_agreements. */
  supplierId: string
  /** agreements.subsidiary_id, when set — a single nullable column, so zero or one edge. */
  subsidiaryScopeId?: string | null
}

export const buildAgreementTuples = (orgId: string, agreement: AgreementTupleInput): FgaTuple[] => {
  const object = agreementObject(agreement.id)
  // A confidential record swaps its organisation and scope edges for the twins — see buildRequestTuples.
  const organisationRelation = agreement.isConfidential ? 'derived_organisation_confidential' : 'derived_organisation'
  const scopeRelation = agreement.isConfidential ? 'derived_scope_confidential' : 'derived_scope'
  const supplierRelation = agreement.isConfidential ? 'derived_supplier_confidential' : 'derived_supplier'
  const ownerUserIds = [...new Set(agreement.ownerUserIds)]
  return [
    { user: organisationObject(orgId), relation: organisationRelation, object },
    { user: supplierObject(agreement.supplierId), relation: supplierRelation, object },
    ...ownerUserIds.map((userId): FgaTuple => ({ user: `user:${userId}`, relation: 'derived_owner', object })),
    ...(agreement.subsidiaryScopeId != null
      ? [{ user: scopeValueObject(agreement.subsidiaryScopeId), relation: scopeRelation, object } satisfies FgaTuple]
      : []),
  ]
}

/**
 * The tuple marking a user's role-derived organisation relation (their legacy role tier;
 * owner for super admins), or null for tiers that hold no organisation role (suppliers).
 */
export const organisationRoleTuple = (orgId: string, user: UserRoleTierConfig & { id: string }): FgaTuple | null => {
  const role = mapUserToOrganisationRole(user)
  return role == null ? null : { user: `user:${user.id}`, relation: role, object: organisationObject(orgId) }
}

/** The member tuple placing a user in an org group. */
export const groupMemberTuple = (userId: string, groupId: string): FgaTuple => ({
  user: `user:${userId}`,
  relation: 'derived_member',
  object: groupObject(groupId),
})

/**
 * The dimension link is kept whether or not the row is archived, so fixed grants stay resolvable;
 * the member tuples are not, which is what makes matching grants on an archived value go dark.
 */
export const buildCoreDataValueTuples = (
  orgId: string,
  value: { id: string; dimension: CoreDataDimension; isArchived: boolean; memberUserIds: readonly string[] }
): FgaTuple[] => {
  const object = scopeValueObject(value.id)
  return [
    { user: scopeDimensionObject(orgId, value.dimension), relation: 'structural_dimension', object },
    ...(value.isArchived
      ? []
      : value.memberUserIds.map((userId): FgaTuple => ({ user: `user:${userId}`, relation: 'derived_member', object }))),
  ]
}

/**
 * The derived_member tuples for a user's own core-data links (users.department_id,
 * users.subsidiary_id). Nulls filtered by the caller passing only present ids.
 */
export const buildUserScopeMembershipTuples = (userId: string, valueIds: readonly string[]): FgaTuple[] =>
  valueIds.map((valueId): FgaTuple => ({ user: `user:${userId}`, relation: 'derived_member', object: scopeValueObject(valueId) }))

// ---------------------------------------------------------------------------
// Structural anchors — bootstrap data, ensured rather than derived
// ---------------------------------------------------------------------------

/** One config_area instance per area. Ensured idempotently, never deleted, not mutable through the API. */
export const buildAnchorTuples = (orgId: string): FgaTuple[] => {
  const org = organisationObject(orgId)
  return CONFIG_AREAS.map(
    (area): FgaTuple => ({ user: org, relation: 'structural_organisation', object: configAreaObject(orgId, area) })
  )
}

/** One scope_dimension instance per dimension, on the same ensure-only terms as {@link buildAnchorTuples}. */
export const buildScopeDimensionAnchorTuples = (orgId: string): FgaTuple[] => {
  const org = organisationObject(orgId)
  return CORE_DATA_DIMENSIONS.map(
    (dimension): FgaTuple => ({
      user: org,
      relation: 'structural_organisation',
      object: scopeDimensionObject(orgId, dimension),
    })
  )
}

/**
 * The super-admin permission_set's org-wide grants, superseded by role-derived owner tuples. Deletes
 * only — the relations and the type are gone from the model, hence plain strings rather than FgaTuple.
 * Membership tuples are not swept here: they sit on the set objects themselves, which the
 * legacy-type sweep drains (permission_set is in LEGACY_OBJECT_TYPES).
 */
export const buildLegacyAnchorTuples = (orgId: string): Array<{ user: string; relation: string; object: string }> => {
  const org = organisationObject(orgId)
  const legacySuperAdminMember = `permission_set:${orgId}/super-admin#member`
  return [
    { user: legacySuperAdminMember, relation: 'config_area_manage', object: org },
    { user: legacySuperAdminMember, relation: 'permission_admin', object: org },
  ]
}
